import type { Channel } from '@conversia/shared-types';

/**
 * AVISOS POR CANAL en el panel — lógica PURA (sin React) de dos pantallas:
 *  - el selector "Canal (de qué vendedora es)" del cliente manual;
 *  - el interruptor "Avisos automáticos" de cada canal en /channels.
 *
 * Cada canal de WhatsApp es el teléfono de una vendedora. El canal del cliente
 * decide desde qué número le llegan sus recordatorios y avisos, y apagar los
 * avisos de un canal corta todo lo automático para sus clientes.
 */

/** Texto EXACTO de la confirmación al apagar: qué deja de salir y qué sigue. */
export const CONFIRMACION_APAGAR_AVISOS =
  'Mientras estén apagados, este canal no le manda recordatorios, avisos de llegada ni encuestas a sus clientes, y no salen avisos internos de sus clientes. La asesora sigue contestando.';

const ERROR_SIN_CANAL =
  'Elige de qué canal (vendedora) es este cliente: de ese número le van a llegar sus recordatorios y avisos.';

/**
 * Los canales que se pueden elegir para un cliente: los de WhatsApp, conectados
 * o no — un teléfono caído hoy sigue siendo el de su vendedora. (La lista del
 * servidor ya viene sin los borrados.)
 */
export function canalesWhatsApp(channels: Channel[]): Channel[] {
  return channels.filter((c) => c.type === 'WHATSAPP');
}

/** Con un solo WhatsApp no hay nada que preguntar: viene elegido. Con varios, vacío. */
export function canalPreseleccionado(channels: Channel[]): string {
  const wa = canalesWhatsApp(channels);
  return wa.length === 1 ? wa[0]!.id : '';
}

/** Espejo de la regla del servidor: con más de un WhatsApp hay que elegir uno de la lista. */
export function validarCanalDelCliente(
  channels: Channel[],
  channelId: string,
): { ok: true } | { ok: false; error: string } {
  const wa = canalesWhatsApp(channels);
  if (!channelId && wa.length <= 1) return { ok: true };
  if (wa.some((c) => c.id === channelId)) return { ok: true };
  return { ok: false, error: ERROR_SIN_CANAL };
}

/**
 * Cómo se muestra el interruptor. Un canal sin el campo (de antes de la
 * migración) cuenta como encendido: es lo que hace el servidor.
 */
export function estadoDeAvisos(
  c: Pick<Channel, 'avisosPausados' | 'avisosPausadosAt' | 'avisosPausadosPorNombre'>,
  timeZone = 'America/Santo_Domingo',
): { encendidos: boolean; etiqueta: string; detalle: string | null } {
  if (!c.avisosPausados) {
    return { encendidos: true, etiqueta: 'Avisos automáticos: Encendidos', detalle: null };
  }
  const partes = ['Apagados'];
  if (c.avisosPausadosPorNombre) partes.push(`por ${c.avisosPausadosPorNombre}`);
  const fecha = c.avisosPausadosAt ? new Date(c.avisosPausadosAt) : null;
  if (fecha && !Number.isNaN(fecha.getTime())) {
    const cuando = new Intl.DateTimeFormat('es-DO', {
      timeZone,
      day: 'numeric',
      month: 'long',
      hour: 'numeric',
      minute: '2-digit',
    }).format(fecha);
    partes.push(`el ${cuando}`);
  }
  return {
    encendidos: false,
    etiqueta: 'Avisos automáticos: Apagados',
    detalle: partes.join(' '),
  };
}
